import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiRefreshCw, FiCheckCircle, FiXCircle, FiMail, FiAlertTriangle } from 'react-icons/fi';

export default function RefundPolicy() {
  const lastUpdated = "January 28, 2026";

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
            <FiArrowLeft className="w-4 h-4" />
            <span>Back to FirstCut</span>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
              <span className="text-white font-bold text-sm">F</span>
            </div>
            <span className="text-white font-semibold">FirstCut</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center">
              <FiRefreshCw className="w-6 h-6 text-violet-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Cancellation & Refund Policy</h1>
              <p className="text-slate-400">Last updated: {lastUpdated}</p>
            </div>
          </div>
        </div>

        <div className="prose prose-invert prose-slate max-w-none">
          {/* Important Notice */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <FiAlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-lg font-semibold text-white m-0 mb-2">Important: No-Refund Policy</h2>
                <p className="text-slate-300 m-0">
                  All purchases on FirstCut are <strong>final and non-refundable</strong>. Please review your plan selection carefully before purchasing. We encourage you to start with our Free tier to evaluate the service before upgrading.
                </p>
              </div>
            </div>
          </div>

          {/* Quick Summary */}
          <div className="bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 border border-violet-500/30 rounded-xl p-6 mb-10">
            <h2 className="text-lg font-semibold text-white mb-4">Quick Summary</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <FiXCircle className="w-5 h-5 text-red-400 mt-0.5" />
                <div>
                  <p className="text-white font-medium">No Refunds</p>
                  <p className="text-slate-400 text-sm">All purchases are final</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <FiCheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                <div>
                  <p className="text-white font-medium">Cancel Anytime</p>
                  <p className="text-slate-400 text-sm">Stop future renewals</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <FiCheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                <div>
                  <p className="text-white font-medium">Keep Access</p>
                  <p className="text-slate-400 text-sm">Until billing period ends</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <FiCheckCircle className="w-5 h-5 text-amber-400 mt-0.5" />
                <div>
                  <p className="text-white font-medium">Exceptions Only</p>
                  <p className="text-slate-400 text-sm">For billing errors or extended outages</p>
                </div>
              </div>
            </div>
          </div>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">1. Subscription Cancellation</h2>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5 space-y-4">
              <div>
                <h3 className="text-white font-medium mb-2">How to Cancel</h3>
                <p className="text-slate-300">
                  You can cancel your subscription anytime by contacting us at <a href="mailto:support@firstcut.me" className="text-violet-400 hover:text-violet-300">support@firstcut.me</a>. Once we process your cancellation request, no further charges will be made.
                </p>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">What Happens When You Cancel</h3>
                <ul className="text-slate-300 space-y-2 list-disc pl-5">
                  <li>Your subscription will remain active until the end of your current billing period</li>
                  <li>You will retain access to all features until the period ends</li>
                  <li>No further charges will be made after cancellation</li>
                  <li>Your content and data will remain accessible while your account is active</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">2. Refund Policy</h2>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5 space-y-4">
              <div>
                <h3 className="text-white font-medium mb-2">General Policy</h3>
                <p className="text-slate-300">
                  <strong>All purchases are final and non-refundable.</strong> This includes monthly subscriptions, annual subscriptions, and any credit pack purchases. We do not offer partial refunds for unused credits or early cancellations.
                </p>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Exceptions (Refund Eligible)</h3>
                <p className="text-slate-300 mb-3">Refunds may be considered ONLY in the following circumstances:</p>
                <ul className="text-slate-300 space-y-2 list-disc pl-5">
                  <li><strong className="text-green-400">Duplicate Charges:</strong> If you were charged multiple times for the same subscription or purchase due to a billing error</li>
                  <li><strong className="text-green-400">Extended Service Outage:</strong> If FirstCut is unavailable for more than 24 consecutive hours due to issues on our end (not scheduled maintenance)</li>
                </ul>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">NOT Eligible for Refund</h3>
                <ul className="text-slate-300 space-y-2 list-disc pl-5">
                  <li>Change of mind or dissatisfaction with the service</li>
                  <li>Unused credits at the end of billing period</li>
                  <li>Early cancellation of subscription</li>
                  <li>Forgetting to cancel before renewal</li>
                  <li>Account termination due to Terms of Service violation</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">3. Credit Policy</h2>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5 space-y-4">
              <div>
                <h3 className="text-white font-medium mb-2">How Credits Work</h3>
                <p className="text-slate-300">
                  FirstCut uses a credit-based system. You purchase credits through subscription plans or one-time credit packs.
                </p>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Subscription Credits</h3>
                <ul className="text-slate-300 space-y-2 list-disc pl-5">
                  <li>Subscription credits are allocated when you purchase a plan</li>
                  <li>These credits are valid for your billing period</li>
                  <li>Unused subscription credits do NOT roll over</li>
                </ul>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Bonus Credits (Credit Packs)</h3>
                <ul className="text-slate-300 space-y-2 list-disc pl-5">
                  <li>Purchased separately as one-time packs</li>
                  <li><strong className="text-emerald-400">Never expire</strong> - use them anytime</li>
                  <li>Used only after subscription credits are exhausted</li>
                  <li>Cannot be transferred or exchanged for cash</li>
                </ul>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Plan Purchases</h3>
                <ul className="text-slate-300 space-y-2 list-disc pl-5">
                  <li>Each plan purchase is a one-time payment (no auto-renewal currently)</li>
                  <li>To continue using the service, purchase a new plan when your credits run out</li>
                  <li>You can purchase a different plan at any time</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">4. Free Tier</h2>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5">
              <p className="text-slate-300">
                FirstCut offers a Free tier with limited credits. We strongly recommend starting with the Free tier to evaluate the service before purchasing a paid subscription. No payment is required for the Free tier, so no refund situation applies.
              </p>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">5. How to Request a Refund (For Eligible Cases)</h2>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5">
              <p className="text-slate-300 mb-4">If you believe you qualify for a refund under the exceptions listed above:</p>
              <ol className="text-slate-300 space-y-3 list-decimal pl-5">
                <li>Email us at <a href="mailto:support@firstcut.me" className="text-violet-400 hover:text-violet-300">support@firstcut.me</a> with subject line "Refund Request"</li>
                <li>Include your account email and the reason for your request</li>
                <li>Provide any relevant documentation (e.g., bank statements showing duplicate charges)</li>
                <li>Our team will review your request within 3-5 business days</li>
                <li>If approved, refunds are processed within 7-10 business days to the original payment method</li>
              </ol>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">6. Payment Processing</h2>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5">
              <ul className="text-slate-300 space-y-3 list-disc pl-5">
                <li><strong>Payment Provider:</strong> All payments are processed securely via Razorpay</li>
                <li><strong>Currency:</strong> All prices are in Indian Rupees (INR)</li>
                <li><strong>Billing Cycle:</strong> Subscriptions are billed monthly from the date of purchase</li>
                <li><strong>Auto-Renewal:</strong> Subscriptions automatically renew unless cancelled</li>
              </ul>
            </div>
          </section>

          <section>
            <div className="flex items-center gap-3 mb-4">
              <FiMail className="w-5 h-5 text-violet-400" />
              <h2 className="text-xl font-semibold text-white m-0">7. Contact Us</h2>
            </div>
            <div className="bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 border border-violet-500/30 rounded-lg p-5">
              <p className="text-slate-300 mb-3">
                For questions about this policy or to request a cancellation:
              </p>
              <ul className="text-slate-300 space-y-2">
                <li><strong>Email:</strong> <a href="mailto:support@firstcut.me" className="text-violet-400 hover:text-violet-300">support@firstcut.me</a></li>
                <li><strong>Response Time:</strong> Within 2-3 business days</li>
                <li><strong>Website:</strong> <a href="https://firstcut.me" className="text-violet-400 hover:text-violet-300">https://firstcut.me</a></li>
              </ul>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center text-slate-500 text-sm">
          <p>© 2026 FirstCut. All rights reserved.</p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/terms" className="hover:text-slate-300 transition-colors">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-slate-300 transition-colors">Privacy Policy</Link>
            <Link to="/refund" className="hover:text-slate-300 transition-colors">Refund Policy</Link>
            <Link to="/contact-us" className="hover:text-slate-300 transition-colors">Contact Us</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
